<template>
  <div class="Collection-FacetsHeader" :class="headerModifier" data-ht-module="filters-bar">
    <div class="page-width Collection-FacetsHeader__Inner">
      <button role="button" class="Collection-FacetsHeader__Toggle" @click="toggleOpenness()" data-ht-element="filters-bar-toggle">
        <Icon icon="input-sliders" />
        <span>
          {{ title }}
        </span>
      </button>
      <button role="button" class="Collection-FacetsHeader__Clear t5" @click="handleRemoveAll()" data-ht-element="filters-bar-clear">
        {{ terms.filters.clear }}
      </button>
    </div>
  </div>
  <div class="Collection-FacetsBar" :class="barModifier" data-ht-module="filters">
    <Transition name="slide">
      <aside v-if="open || isDesktop" class="Collection-FacetsBar__Inner">
        <div class="relative">
          <h2 class="t1 Collection-FacetsBar__Inner-Title">
            <Icon icon="input-sliders" />
            <span>
              {{ title }}
            </span>
          </h2>
          <button role="button" class="Collection-FacetsBar__Close" @click="toggleOpenness()" data-ht-element="filters-close">
            <Icon icon="close" />
          </button>
        </div>
        <AppliedFacets :appliedFacets="selected" :facets="props.facets" :terms="props.terms" @remove-facet="handleSelection" @remove-all-facets="handleRemoveAll" />
        <AccordionList open-multiple-items>
          <AccordionItem default-opened v-for="facet in facets" :key="facet.id" data-ht-component="filters-category" :data-ht-meta-title="facet.label">
            <template #summary>
              <span class="t2" data-ht-element="category-toggle">
                {{ facet.label }}
              </span>
            </template>
            <template #icon>
              <span data-ht-element="category-toggle">
                <Icon icon="qv-plus" />
              </span>
            </template>
            <div class="Facets-Wrap" :class="facetModifier(facet)">
              <Facet v-for="value in facet.values" :key="value.label" :value="value" :id="facet.id" @handle-selection="handleSelection" :isSelected="isSelected(value, facet)" />
            </div>
          </AccordionItem>
        </AccordionList>
        <div class="Collection-FacetsSubmit" :class="submitModifier">
          <button role="button" @click="toggleOpenness()">
            <span class="t5">
              {{ apply }}
            </span>
          </button>
        </div>
      </aside>
    </Transition>
  </div>
</template>

<script setup>
import { watch, ref, computed, onBeforeMount } from 'vue';
import { pubSub } from '@utils/PubSub';
import { objectsEqual, deepClone } from '@utils/performance';
import { AccordionList, AccordionItem } from 'vue3-rich-accordion';

import Icon from '../Global/Icon.vue';
import Facet from './Facet.vue';
import AppliedFacets from './AppliedFacets.vue';

const emits = defineEmits(['apply-filters', 'reset-cursor']);
const props = defineProps({
  facets: Array,
  terms: Object,
});

const open = ref(false);
const selected = ref([]);
const scrollPosition = ref(0);
const isDesktop = ref(false);

const selectFromUrl = () => {
  if (props.facets.length <= 0) return;
  console.log('Selecting Filters from URL...');

  const paramsFromUrl = new URLSearchParams(window.location.search);
  const preappliedFacets = Array.from(paramsFromUrl.entries())
    .filter(([key]) => key.includes('filter'))
    .map(([key, value]) => {
      return { [key]: value };
    })
    .filter((obj) => Object.keys(obj).length !== 0);

  const selections = [];

  if (preappliedFacets.length <= 0) {
    selected.value = selections;
    emits('apply-filters', selections);
    return;
  }

  preappliedFacets.forEach((facet, index) => {
    const data = Object.entries(facet)[0];
    const key = data[0];
    const value = data[1];
    const filter = props.facets.find((facet) => facet.id === key);
    selections.push({
      [filter.id]: value,
    });
  });

  selected.value = selections;
  emits('apply-filters', selected.value);
};

const updateUrl = (selection, adding = false) => {
  const data = Object.entries(selection)[0];
  const parameter = data[0];
  const value = data[1];
  const params = new URLSearchParams(window.location.search);

  const alreadyApplied = params.has(parameter, value);

  if (alreadyApplied && adding) {
    console.log(`${parameter} ${value} Already added.`);
    return;
  }

  if (adding) {
    params.append(parameter, value);
  } else {
    params.delete(parameter, value);
  }

  const url = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', url);
  selectFromUrl();
};

const outputtedMetaobjectId = (value) => {
  const id = value.id.split('gid-shopify-metaobject-')[1].trim();
  return `gid://shopify/Metaobject/${id}`;
};

const title = computed(() => {
  const title = props.terms?.filters?.title ?? '';
  const count = selected.value.length;
  return count > 0 ? `${title} (${count})` : title;
});

const apply = computed(() => {
  const title = props.terms?.filters?.apply ?? '';
  const count = selected.value.length;
  return count > 0 ? `${title} (${count})` : title;
});

const isSelected = computed(() => {
  return (value, facet) => {
    const data = {};
    const id = facet.id;
    const isColor = id.includes('color');

    data[id] = isColor ? outputtedMetaobjectId(value) : value.label;
    const filterApplied = selected.value.find((filter) => {
      const facet = deepClone(filter);
      delete facet.applied;

      return objectsEqual(facet, data);
    });

    return filterApplied ? true : false;
  };
});

const handleSelection = (selection) => {
  const data = deepClone(selection);
  const addSelection = data.applied;
  scrollPosition.value = 0;
  updateUrl(selection, addSelection);
  if (isDesktop.value) {
    scrollToTop();
  }
};

const scrollToTop = () => {
  window.scrollTo({
    top: scrollPosition.value,
    left: 0,
    behavior: 'smooth',
  });
};

const handleRemoveAll = () => {
  new Promise((resolve) => {
    emits('reset-cursor');
    resolve();
  }).then(() => {
    selected.value.forEach((selection) => handleSelection(selection));
    scrollPosition.value = 0;
    scrollToTop();
  });
};

const toggleOpenness = () => {
  const isOpen = !open.value;
  if (isOpen) {
    scrollPosition.value = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition.value}px`;
  } else {
    document.body.style.position = '';
    document.body.style.top = '';
    scrollToTop();
  }
  open.value = isOpen;
};

const handleViewport = () => {
  isDesktop.value = window.innerWidth > 1024;
};

const facetModifier = computed(() => {
  return (facet) => {
    const label = facet.label.replace(' ', '-');
    return `Facets-Wrap--${label}`;
  };
});

const headerModifier = computed(() => {
  const hasAppliedFacets = selected.value.length > 0;
  return hasAppliedFacets ? 'Collection-FacetsHeader--Active' : '';
});

const barModifier = computed(() => {
  return open.value ? 'Collection-FacetsBar--Active' : '';
});

const submitModifier = computed(() => {
  const requiresSelections = selected.value.length <= 0;
  return requiresSelections ? 'Collection-FacetsSubmit--Disabled' : '';
});

onBeforeMount(() => {
  handleViewport();
  window.addEventListener('resize', handleViewport);
});

pubSub.subscribe('plp:clear-filters', handleRemoveAll);
watch(() => props.facets, selectFromUrl, { immediate: true });
</script>

<style lang="css" scoped>
.slide-enter-active {
  transition-delay: 400ms;
  transition: transform 300ms cubic-bezier(0, 0.55, 0.45, 1);
}
@media screen and (max-width: 1024px) {
  .slide-enter-from,
  .slide-leave-to {
    transform: translateX(100%);
  }
}
</style>
